'use client';

import React from 'react';
import { Button } from '@/components/ui/button';
import { ImageIcon, SortDesc, StarIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProductReviewElementDataType } from '@/components/sections/builder/elements/body/product-review/type';

export const SORTING_QUERY = [
  {
    label: 'Date, old to new',
    value: 'created-ascending',
    sortBy: 'created_at',
    sortDirection: 'ASC',
  },
  {
    label: 'Date, new to old',
    value: 'created-descending',
    sortBy: 'created_at',
    sortDirection: 'DESC',
  },
  {
    label: 'Rating, low to high',
    value: 'rating-ascending',
    sortBy: 'rating',
    sortDirection: 'ASC',
  },
  {
    label: 'Rating, high to low',
    value: 'rating-descending',
    sortBy: 'rating',
    sortDirection: 'DESC',
  },
];

export type FilterSortType = {
  rating?: number;
};

interface FilterProductReviewProps {
  filter: FilterSortType;
  handleRatingFilter: (rating: number) => void;
  isPreview?: boolean;
  updateSortBy: (sortBy: string) => void;
  currentSorting: string;
  withMedia?: boolean;
  onMediaFilterToggle?: () => void;
  mediaReviewCount?: number;
  content: ProductReviewElementDataType;
  starColor?: string;
}

export default function FilterProductReview({
  filter,
  handleRatingFilter,
  updateSortBy,
  currentSorting,
  withMedia = false,
  onMediaFilterToggle,
  mediaReviewCount = 0,
  content,
  starColor,
}: FilterProductReviewProps) {
  return (
    <div className='hidden w-full items-center sm:flex'>
      <div className='flex items-center gap-4'>
        {content.showFilter && (
          <>
            <span className='font-medium'>Filter:</span>
            <div className='flex gap-x-3'>
              <div className='flex flex-col'>
                <div className='flex w-full items-center gap-2'>
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const isSelected = filter.rating === rating;
                    return (
                      <Button
                        key={rating}
                        variant={isSelected ? 'default' : 'outline'}
                        className='gap-x-1 px-[11px]'
                        onClick={() => handleRatingFilter(rating)}
                        style={
                          isSelected
                            ? {
                                backgroundColor: content.colorScheme?.btnSolidBackgroud,
                                color: content.colorScheme?.btnSolidlabel,
                              }
                            : {}
                        }
                      >
                        <StarIcon
                          className='h-4 w-4'
                          style={{
                            color: starColor,
                            fill: starColor,
                          }}
                        />
                        <span>{rating}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>
              {onMediaFilterToggle && (
                <Button
                  variant={withMedia ? 'default' : 'outline'}
                  onClick={onMediaFilterToggle}
                  className='gap-x-2'
                  style={
                    withMedia
                      ? {
                          backgroundColor: content.colorScheme?.btnSolidBackgroud,
                          color: content.colorScheme?.btnSolidlabel,
                        }
                      : {}
                  }
                >
                  <ImageIcon className='h-4 w-4' />
                  <span>With Photos</span>
                  {withMedia && mediaReviewCount > 0 && (
                    <span className='rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-900'>
                      {mediaReviewCount}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <div className='flex flex-1 items-center justify-end '>
        {content.showSorting && (
          <div className='flex flex-col gap-3 md:flex-row md:items-center'>
            <SortDesc className='text-sm' />
            <span className='text-sm'>Sort by:</span>
            <Select value={currentSorting} onValueChange={updateSortBy}>
              <SelectTrigger className='w-full md:w-48'>
                <SelectValue placeholder='Sort by' />
              </SelectTrigger>
              <SelectContent className='z-[110] w-full'>
                {SORTING_QUERY.map((item, index) => (
                  <SelectItem key={index} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}
